// tslint:disable:no-non-null-assertion
import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot, RouteConfigLoadEnd } from "@angular/router";
import { Injectable } from "@angular/core";

export interface OnDetach {
  ngOnDetach();
}
export interface OnAttach {
  ngOnAttach(route: ActivatedRouteSnapshot);
}

export interface IRouteReuseConfigData {
  reuse: boolean;
}

@Injectable()
export class CacheRouteReuseStrategy implements RouteReuseStrategy {
  private readonly storedRouteHandles = new Map<string, DetachedRouteHandle>();

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const data = this.getRouteData(route);
    return data && data.reuse;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const url = this.getRouteUrl(route);

    this.storedRouteHandles.set(url, handle);

    const component = handle && (handle as any).componentRef.instance;
    const detachHook = component && component.ngOnDetach;
    if (detachHook != null) {
      detachHook.call(component);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const url = this.getRouteUrl(route);
    return this.storedRouteHandles.has(url);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const url = this.getRouteUrl(route);
    const data = this.getRouteData(route);

    const handle = this.storedRouteHandles.get(url);

    if (data && data.reuse && handle != null) {
      const component = (handle as any).componentRef.instance;
      const attachHook = component && component.ngOnAttach;
      (route as any).routeConfig = (handle as any).route.value.routeConfig;
      if (attachHook != null) {
        attachHook.call(component, route);
      }
      return handle;
    }
    return null;
  }

  private getRouteUrl(route: ActivatedRouteSnapshot): string {
    return this.getRouteUrlPaths(route).filter(Boolean).join("/");
  }

  private getFullRouteUrl(route: ActivatedRouteSnapshot): string {
    return this.getFullRouteUrlPaths(route).filter(Boolean).join("/");
  }

  private getFullRouteUrlPaths(route: ActivatedRouteSnapshot): string[] {
    const paths = this.getRouteUrlPaths(route);
    return route.parent ? [...this.getFullRouteUrlPaths(route.parent), ...paths] : paths;
  }

  private getRouteUrlPaths(route: ActivatedRouteSnapshot): string[] {
    return route.url.map((urlSegment) => urlSegment.path);
  }

  private getRouteData(route: ActivatedRouteSnapshot): IRouteReuseConfigData {
    return (route.routeConfig && route.routeConfig.data) as IRouteReuseConfigData;
  }
}
